// Angular
import { CommonModule, registerLocaleData } from "@angular/common";
import { HttpClientJsonpModule, HttpClientModule, provideHttpClient, withInterceptors } from "@angular/common/http";
import { CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule, APP_INITIALIZER, LOCALE_ID } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { RouterModule, Routes } from "@angular/router";
import { MessageService, ConfirmationService } from "primeng/api";
import { ToastModule } from "primeng/toast";
import { ConfirmDialogModule } from "primeng/confirmdialog";

// BBWT Module
import { BBWTComponent } from "./components/bbwt.component";
import { SecurityGuard, SecurityService } from "./modules/security";
import { GlobalErrorHandler, CurrentUserWatchService } from "./modules/logging";
import {
    browserInfoInterceptor,
    emptyResponseBodyInterceptor,
    HttpResponsesHandlersFactory,
    httpResponseSimulationInterceptor,
    httpStatusesInterceptor,
    apiInterceptor,
    serviceWorkerBypassInterceptor,
    datesConversionInterceptor
} from "./modules/data-service";

// BBWT Modules references
import { AdminService } from "@main/admin/services";
import { ApiVersionService, apiVersionInterceptor } from "@main/app-layout";
import { SystemConfigurationService, SystemConfigurationResolver } from "@main/system-configuration";
import { UserService } from "@main/users";
import { RoleService } from "@main/roles";
import { PwaModule } from "@main/pwa/pwa.module";
import { PwaService } from "@main/pwa/pwa.service";
import defaultLocale from "@angular/common/locales/en-GB";
import { UserHelperService } from "@main/users/user-helper.service";
import { AppVersioningModule } from "@main/app-versioning/app-versioning.module";
import { NamingConventionService } from "@mipro/services/naming-convention.service";
import { RavenApiService } from "@main/raven/raven-api.service";
import { AppPwaCacheService } from "@main/pwa/cache/app-pwa-cache.service";
import { patientSelectedContextVariableInterceptor } from "@main/reporting.v3/query-builder/api/patient-selected-context-variable.interceptor";
import { autoSaveInterceptor } from "@mipro/services/auto-save.interceptor";
import { NavigationHistoryService } from "@mipro/services/navigation-history.service";
import { EnhancedRouterService } from "@mipro/services/enhanced-router.service";
import { MultiUserFormSetupModule } from "@main/formio/multi-user-form/multi-user-form-setup.module";

registerLocaleData(defaultLocale);

@NgModule({
    declarations: [BBWTComponent],
    imports: [
        CommonModule,
        BrowserModule,
        HttpClientModule,
        HttpClientJsonpModule,
        FormsModule,
        BrowserAnimationsModule,
        ReactiveFormsModule,
        ToastModule,
        ConfirmDialogModule,
        MultiUserFormSetupModule,
        AppVersioningModule,
        PwaModule,

        // Routes
        RouterModule.forRoot(
            <Routes>[
                { path: "account", loadChildren: () => import("../account/account.module").then(m => m.AccountModule) },
                {
                    path: "shared-file",
                    loadChildren: () => import("../project/shared-file/shared-file.module").then(m => m.SharedFileModule)
                },
                {
                    path: "selfregistration",
                    loadChildren: () => import("../project/self-registration/self-registration.module").then(m => m.SelfRegistrationModule)
                },
                {
                    path: "book-appointment",
                    loadChildren: () => import("../project/appointment-booking/appointment-booking.module").then(m => m.AppointmentBookingModule)
                },
                {
                    path: "testorder",
                    loadChildren: () => import("../project/test-order/test-order-no-login.module").then(m => m.TestOrderNoLoginModule)
                },
                {
                    path: "attach-file",
                    loadChildren: () => import("../project/attach-file-by-url/attach-file.module").then(m => m.AttachFileModule)
                },
                {
                    path: "document-upload/:requestId",
                    loadComponent: () => import("../project/document-upload-request/upload/upload.component").then(c => c.UploadComponent)
                },
                {
                    path: "app",
                    loadChildren: () => import("../main/main.module").then(m => m.MainModule),
                    canLoad: [SecurityGuard],
                    resolve: { sysConfig: SystemConfigurationResolver }
                },
                {
                    path: "static",
                    loadChildren: () => import("../main/static-pages/static-pages.module").then(m => m.StaticPagesModule)
                },
                { path: "**", redirectTo: "app" },
                { path: "", redirectTo: "app", pathMatch: "full" },
                { path: "saml", redirectTo: "saml" },
                { path: "saml/login", redirectTo: "saml/login" }
            ],
            { scrollPositionRestoration: "enabled", bindToComponentInputs: true }
        )
    ],
    providers: [
        AdminService,
        ApiVersionService,
        ConfirmationService,
        CurrentUserWatchService,
        HttpResponsesHandlersFactory,
        MessageService,
        SecurityGuard,

        { provide: LOCALE_ID, useValue: "en-GB" },
        provideHttpClient(
            withInterceptors([
                emptyResponseBodyInterceptor,
                httpStatusesInterceptor,
                apiVersionInterceptor,
                apiInterceptor,
                serviceWorkerBypassInterceptor,
                datesConversionInterceptor,
                httpResponseSimulationInterceptor,
                browserInfoInterceptor,
                patientSelectedContextVariableInterceptor,
                autoSaveInterceptor
            ])
        ),

        { provide: ErrorHandler, useClass: GlobalErrorHandler },

        {
            provide: APP_INITIALIZER,
            useFactory: (userService: UserService) => () => userService.initialize(),
            deps: [UserService],
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (service: SystemConfigurationService) => () => service.initialize(),
            deps: [SystemConfigurationService],
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (service: AdminService) => () => service.refreshCurrentUserAdminState(),
            deps: [AdminService],
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (service: CurrentUserWatchService) => () => service.refreshLoggers(),
            deps: [CurrentUserWatchService],
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (service: RoleService) => () => service.initialize(),
            deps: [RoleService],
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (service: PwaService) => () => service.initPwaPrompt(),
            deps: [PwaService],
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (pwaCacheService: AppPwaCacheService) => () => pwaCacheService.initialize(),
            deps: [AppPwaCacheService],
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (userHelper: UserHelperService) => () => userHelper.initialize(),
            deps: [UserHelperService],
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (namingConventionService: NamingConventionService) => () => namingConventionService.initialize(),
            deps: [NamingConventionService],
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (ravenApi: RavenApiService) => () => ravenApi.initialize(),
            deps: [RavenApiService],
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (service: SecurityService) => () => service.initialize(),
            deps: [SecurityService],
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (navigationHistoryService: NavigationHistoryService) => () => navigationHistoryService.initialize(),
            deps: [NavigationHistoryService],
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (enhancedRouterService: EnhancedRouterService) => () => enhancedRouterService.initialize(),
            deps: [EnhancedRouterService],
            multi: true
        }
    ],
    bootstrap: [BBWTComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class BBWTModule {}
